import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import API from "../../../service/api";
import { MDBIcon } from "mdb-react-ui-kit";
import { AdminPage } from "../AdminPage";
import { DataTable, DataTableStringComparison } from "../../../components/DataTable";
import { ProductEdit } from "./ProductEdit";
import { SplitScreenDiv } from "../../../components/SplitScreenDiv";
import { SearchInput } from "../../../components/SearchInput";


export interface ProductsPageProps {
    canAdd: boolean;
    canEdit: boolean;
    canDelete: boolean;
}
export const ProductsPage = ({canAdd, canEdit, canDelete}) => {
    const [selectedId, setSelectedId] = useState<number|null>(null);
    const [data, setData] = useState([]);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const { t } = useTranslation();

    const fetchProducts = useCallback(() => {
        API.GET('/api/products')
            .then(data => setData(data))
            .catch(err => {
                setError(err.message);
                setData([]);
            });
    }, []);

    useEffect(() => {
        fetchProducts();
    }, []);

    const Table = useMemo(() => (
        <DataTable<any>
            data={data}
            height={500}
            onRowSelected={p => setSelectedId(p.id)}
            search={search}
            columns={[
                {headerName: t('name'), valueGetter: ({data}) => ({name: data.name, description: data.description}), flex: 3,
                    cellRenderer: ({value}) => (
                        <span className="d-flex flex-row align-items-center w-100 h-100">
                            <MDBIcon icon="shopping-cart" color="primary" className="me-1"/>
                            <div className="d-flex flex-column" style={{lineHeight: value.description ? '0.8rem' : '1rem'}}>
                                <span>{value.name}</span>
                                <span className="text-muted" style={{fontSize: '80%'}}>{value.description}</span>
                            </div>
                        </span>
                    ),
                    comparator: (row1, row2) => DataTableStringComparison(row1.data.name, row2.data.name),
                    quickFilterText: data => data.name + data.description
                },
                {headerName: t('aqprice_short'), field: 'aqPrice', flex: 1, cellRenderer: ({value}) => (
                    <span>
                        <MDBIcon icon="money-bill-alt" color="success" className="me-1"/>
                        {value}
                    </span>
                )}
            ]}
        />
    ), [data, search]);

    const ProductEditComponent = useMemo(() => typeof selectedId !== 'number' ? null : (
        <ProductEdit
            productId={selectedId}
            canEdit={canEdit}
            canDelete={canDelete}
            onAdd={() => { fetchProducts(); setSelectedId(null);}}
            onEdit={() => fetchProducts()}
            onClose={() => setSelectedId(null)}
        />
    ), [selectedId, canEdit, canDelete, fetchProducts]);

    return (
        <AdminPage
            title={t('products')}
            buttons={!canAdd ? undefined : {title: t('add_product'), icon: 'plus-circle', onClick: () => setSelectedId(-1)}}
            error={error}
        >
            <SearchInput onSearch={setSearch}/>
            <SplitScreenDiv
                ratio={60}
                left={Table}
                right={ProductEditComponent}
            />
        </AdminPage>
    )
}